import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';

export type TelemetryLevel = 'info' | 'warn' | 'error';

export interface TelemetryEvent {
  event: string;
  message: string;
  level: TelemetryLevel;
  channel: string;
  attributes?: Record<string, unknown>;
  visibility?: 'fleet' | 'internal';
  cap?: number;
}

const DEFAULT_CAP = 50;

// Sentry Logs has no typed form for null — it serializes to the STRING "null", which mixes
// types on the very fields that split the cohorts. An absent key queries cleanly instead.
export const logsAttributes = <T extends object>(payload: T): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
  );

// Per-event, module-level: must survive reconnects, since a reconnect loop is the
// failure a cap protects against. Never store this on a reconnect-reset instance field.
const seqByEvent = new Map<string, number>();

export const emitTelemetryEvent = ({
  event,
  message,
  level,
  channel,
  attributes = {},
  visibility = 'fleet',
  cap = DEFAULT_CAP,
}: TelemetryEvent): void => {
  const seq = (seqByEvent.get(event) ?? 0) + 1;
  seqByEvent.set(event, seq);
  const spent = seq > cap;

  const record = { ...attributes, seq, release: process.env.REACT_APP_RELEASE_VERSION };

  // The session log is per-session forensics, where repetition is the signal — a storm truncated at
  // the cap reads as "it stopped". Only the fleet-wide surfaces are throttled: Sentry Logs, whose
  // aggregates a single wedged client would distort, and breadcrumbs, which share a 500-slot ring
  // with every other subsystem's trail.
  logger
    .remote({
      system: true,
      capture: spent ? undefined : channel,
      nonProd: visibility === 'internal',
    })
    [level](message, record);

  if (spent) {
    return;
  }

  // Safe no-op when Sentry.init never ran (sentryEnabled=false rooms).
  Sentry.logger[level](message, logsAttributes({ ...record, event }));
};

export const resetTelemetryCaps = (): void => {
  seqByEvent.clear();
};
